import { ProcessedLocationData } from './PriceGeographyChart.types'

export const formatPrice = (
  price: number,
  isRental: boolean = false
): string => {
  if (isNaN(price) || price === 0) return '0 CHF'

  const formatter = new Intl.NumberFormat('en-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const formatted = formatter.format(Math.round(price))

  // For display in charts, use short format
  if (price >= 1000000) {
    const millions = price / 1000000
    return `${millions.toFixed(millions >= 10 ? 0 : 1)}M CHF`
  } else if (price >= 1000) {
    const thousands = price / 1000
    return `${thousands.toFixed(thousands >= 10 ? 0 : 1)}K CHF`
  }

  if (isRental) {
    return `${formatted}/m`
  }

  return formatted
}

export const formatPricePerSqm = (price: number): string => {
  if (isNaN(price) || price === 0) return '0 CHF/m²'

  const rounded = Math.round(price)
  return `${rounded.toLocaleString()} CHF/m²`
}

export const getColor = (
  price: number,
  thresholds: { p90: number; p70: number; p30: number }
): string => {
  if (price >= thresholds.p90) return '#ef4444' // High (top 10%) - Red
  if (price >= thresholds.p70) return '#f59e0b' // Medium-High (10-30%) - Orange
  if (price >= thresholds.p30) return '#eab308' // Medium (30-70%) - Yellow
  return '#22c55e' // Low (bottom 30%) - Green
}

export const getColorSqm = (
  pricePerSqm: number,
  thresholds: { p90: number; p70: number; p30: number }
): string => {
  if (pricePerSqm >= thresholds.p90) return '#ef4444' // High (top 10%) - Red
  if (pricePerSqm >= thresholds.p70) return '#f59e0b' // Medium-High (10-30%) - Orange
  if (pricePerSqm >= thresholds.p30) return '#eab308' // Medium (30-70%) - Yellow
  return '#22c55e' // Low (bottom 30%) - Green
}

export const processLocationData = (
  buckets: any[],
  _type: 'canton' | 'district' | 'municipality'
): ProcessedLocationData[] => {
  return buckets.map((bucket) => ({
    name: bucket.key,
    value: bucket.median_price.values['50.0'],
    medianPrice: bucket.median_price.values['50.0'],
    avgPrice: bucket.avg_price?.value || 0,
    listingCount: bucket.listing_count.value,
    medianPricePerSqm: bucket.median_price_per_sqm?.values['50.0'] || 0,
    avgPricePerSqm: bucket.avg_price_per_sqm?.value || 0,
  }))
}

export const calculatePriceThresholds = (
  prices: number[]
): { p90: number; p70: number; p30: number } => {
  const sorted = [...prices].sort((a, b) => a - b)
  const len = sorted.length

  return {
    p90: sorted[Math.floor(len * 0.9)] || 0,
    p70: sorted[Math.floor(len * 0.7)] || 0,
    p30: sorted[Math.floor(len * 0.3)] || 0,
  }
}

export interface LegendItem {
  color: string
  label: string
}

export const getPriceGeographyLegendItems = (
  data: ProcessedLocationData[],
  priceType: 'median' | 'average' = 'median',
  isRental: boolean = false
): LegendItem[] => {
  const prices = data
    .map((d) => (priceType === 'median' ? d.medianPrice : d.avgPrice))
    .filter((p) => p > 0)
  const thresholds = calculatePriceThresholds(prices)

  return [
    {
      color: '#ef4444',
      label: `High Markets (>${formatPrice(thresholds.p90, isRental)})`,
    },
    {
      color: '#f59e0b',
      label: `Medium-High Markets (${formatPrice(thresholds.p70, isRental)}-${formatPrice(thresholds.p90, isRental)})`,
    },
    {
      color: '#eab308',
      label: `Medium Markets (${formatPrice(thresholds.p30, isRental)}-${formatPrice(thresholds.p70, isRental)})`,
    },
    {
      color: '#22c55e',
      label: `Low Markets (<${formatPrice(thresholds.p30, isRental)})`,
    },
  ]
}

export const getPricePerSqmLegendItems = (
  data: ProcessedLocationData[],
  priceType: 'median' | 'average' = 'median'
): LegendItem[] => {
  const prices = data
    .map((d) =>
      priceType === 'median' ? d.medianPricePerSqm : d.avgPricePerSqm
    )
    .filter((p) => p > 0)
  const thresholds = calculatePriceThresholds(prices)

  return [
    {
      color: '#ef4444',
      label: `High (>${formatPricePerSqm(thresholds.p90)})`,
    },
    {
      color: '#f59e0b',
      label: `Medium-High (${formatPricePerSqm(thresholds.p70)}-${formatPricePerSqm(thresholds.p90)})`,
    },
    {
      color: '#eab308',
      label: `Medium (${formatPricePerSqm(thresholds.p30)}-${formatPricePerSqm(thresholds.p70)})`,
    },
    {
      color: '#22c55e',
      label: `Low (<${formatPricePerSqm(thresholds.p30)})`,
    },
  ]
}

type TranslationFn = (key: string, options?: Record<string, unknown>) => string

const defaultT: TranslationFn = (key: string) => {
  const parts = key.split('.')
  return parts[parts.length - 1] || key
}

export const generateInsights = (
  data: ProcessedLocationData[],
  priceType: 'median' | 'average' = 'median',
  nationalPrice: number,
  nationalPricePerSqm: number, // Currently unused
  isRental: boolean = false,
  t: TranslationFn = defaultT
): {
  title: string
  description: string
}[] => {
  const insights: { title: string; description: string }[] = []

  if (data.length === 0) return insights

  // Sort by the selected price type
  const sortedByPrice = [...data].sort((a, b) => {
    const aPrice = priceType === 'median' ? a.medianPrice : a.avgPrice
    const bPrice = priceType === 'median' ? b.medianPrice : b.avgPrice
    return bPrice - aPrice
  })

  const sortedByPricePerSqm = [...data].sort((a, b) => {
    const aPrice =
      priceType === 'median' ? a.medianPricePerSqm : a.avgPricePerSqm
    const bPrice =
      priceType === 'median' ? b.medianPricePerSqm : b.avgPricePerSqm
    return bPrice - aPrice
  })

  // 1. Price disparity analysis
  const highest = sortedByPrice[0]
  const lowest = sortedByPrice[sortedByPrice.length - 1]
  const highestPrice =
    priceType === 'median' ? highest.medianPrice : highest.avgPrice
  const lowestPrice =
    priceType === 'median' ? lowest.medianPrice : lowest.avgPrice
  const disparity = ((highestPrice - lowestPrice) / lowestPrice) * 100

  insights.push({
    title: t('charts.insights.geography.regionalPriceDisparity'),
    description: t('charts.insights.descriptions.geography.regionalPriceDisparity', {
      location1: highest.name,
      location2: lowest.name,
      priceDiff: disparity.toFixed(0),
    }),
  })

  // 2. Market concentration
  const totalListings = data.reduce((sum, d) => sum + d.listingCount, 0)
  const topMarkets = sortedByPrice.slice(0, 3)
  const topMarketsListings = topMarkets.reduce(
    (sum, d) => sum + d.listingCount,
    0
  )
  const concentrationPercent = (topMarketsListings / totalListings) * 100

  const concentrationLevel = concentrationPercent > 60
    ? t('charts.common.high') || 'high'
    : concentrationPercent > 40
      ? t('charts.common.moderate') || 'moderate'
      : t('charts.common.low') || 'low'
  insights.push({
    title: t('charts.insights.geography.marketConcentration'),
    description: t('charts.insights.descriptions.geography.marketConcentration', {
      percentage: concentrationPercent.toFixed(0),
      concentration: concentrationLevel,
    }),
  })

  // 3. Price per m² efficiency
  if (
    sortedByPricePerSqm[0] &&
    sortedByPricePerSqm[0].name !== sortedByPrice[0].name
  ) {
    const highestPricePerSqm =
      priceType === 'median'
        ? sortedByPricePerSqm[0].medianPricePerSqm
        : sortedByPricePerSqm[0].avgPricePerSqm
    insights.push({
      title: t('charts.insights.geography.valueDensityLeader'),
      description: t('charts.insights.descriptions.geography.valueDensityLeader', {
        location: sortedByPricePerSqm[0].name,
        priceM2: formatPricePerSqm(highestPricePerSqm),
      }),
    })
  }

  // 4. National comparison
  const aboveNational = data.filter((d) => {
    const price = priceType === 'median' ? d.medianPrice : d.avgPrice
    return price > nationalPrice
  })

  insights.push({
    title: t('charts.insights.geography.nationalBenchmark'),
    description: t('charts.insights.descriptions.geography.nationalBenchmark', {
      count: aboveNational.length,
      total: data.length,
      average: formatPrice(nationalPrice, isRental),
    }),
  })

  // 5. Price per m² variations
  const pricesPerSqm = data
    .map((d) =>
      priceType === 'median' ? d.medianPricePerSqm : d.avgPricePerSqm
    )
    .filter((p) => p > 0)
  if (pricesPerSqm.length > 0) {
    const avgPricePerSqm =
      pricesPerSqm.reduce((a, b) => a + b, 0) / pricesPerSqm.length
    const deviations = pricesPerSqm.map((p) => Math.abs(p - avgPricePerSqm))
    const avgDeviation =
      deviations.reduce((a, b) => a + b, 0) / deviations.length
    const coefficientOfVariation = (avgDeviation / avgPricePerSqm) * 100

    const consistency =
      coefficientOfVariation < 15
        ? t('charts.common.high') || 'high'
        : coefficientOfVariation < 30
          ? t('charts.common.moderate') || 'moderate'
          : t('charts.common.low') || 'low'
    const consistencyType =
      coefficientOfVariation < 15
        ? t('charts.common.consistency') || 'consistency'
        : t('charts.common.variation') || 'variation'

    insights.push({
      title: t('charts.insights.geography.priceM2Consistency'),
      description: t('charts.insights.descriptions.geography.priceM2Consistency', {
        consistency,
        variation: coefficientOfVariation.toFixed(0),
      }),
    })
  }

  return insights
}
